import React from 'react';
import { formatDublinDate } from '../../utils/date';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useActiveStudent } from '../../hooks/useActiveStudent';
import { useGetMyIncidentQuery, useListMyChildIncidentsQuery } from '../../store/api/localApi';
import IncidentSeverityChip from '../../components/incidents/IncidentSeverityChip';
import IncidentAcknowledgeCard from '../../components/incidents/IncidentAcknowledgeCard';

function formatDate(iso: string): string {
  return formatDublinDate(iso);
}

/**
 * /parent/:studentId/incidents — full incident list for one child, and
 * /parent/:studentId/incidents/:incidentId — the detail + acknowledge view.
 *
 * One component covers both, same as the kid-as-frame convention elsewhere
 * (e.g. ParentChildForms handles its own list; the fill/detail step is a
 * separate route+component only where the flow is genuinely a different
 * page). Here the detail is a lightweight overlay of the same data source
 * (`useGetMyIncidentQuery`), so keeping list+detail in one file avoids a
 * near-empty second file whose only job is a param check.
 */
const ParentChildIncidents: React.FC = () => {
  const { incidentId } = useParams<{ incidentId?: string }>();
  const { activeStudent, isLoading, parentHomePath } = useActiveStudent();

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (!activeStudent) {
    return <Navigate to={parentHomePath ?? '/parent'} replace />;
  }

  return incidentId ? (
    <IncidentDetailView studentId={activeStudent.id} incidentId={incidentId} />
  ) : (
    <IncidentListView studentId={activeStudent.id} childFirstName={activeStudent.firstName} />
  );
};

const IncidentListView: React.FC<{ studentId: string; childFirstName?: string }> = ({
  studentId,
  childFirstName,
}) => {
  const { data, isLoading } = useListMyChildIncidentsQuery({ childId: studentId });
  const incidents = data?.incidents ?? [];

  return (
    <div className="py-6">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
        <div>
          <Link
            to={`/parent/${studentId}`}
            data-testid="parent-incidents-back"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to {childFirstName || 'profile'}
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-gray-900">Incident reports</h1>
          <p className="text-sm text-gray-500">For {childFirstName || 'this child'}</p>
        </div>

        <section data-testid="parent-incidents-list" className="bg-white rounded-lg shadow">
          {isLoading ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">Loading…</div>
          ) : incidents.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              <CheckCircleIcon aria-hidden="true" className="h-6 w-6 mx-auto mb-2 text-green-500" />
              No incident reports.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {incidents.map((incident, idx) => (
                <li key={incident.id}>
                  <Link
                    to={`/parent/${studentId}/incidents/${incident.id}`}
                    data-testid={`parent-incident-row-${idx}`}
                    className="block px-4 py-3 hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      <ExclamationTriangleIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {incident.description}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(incident.occurredAt)}
                        </div>
                      </div>
                      <IncidentSeverityChip severity={incident.severity} />
                      <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

const IncidentDetailView: React.FC<{ studentId: string; incidentId: string }> = ({
  studentId,
  incidentId,
}) => {
  const { data, isLoading, error } = useGetMyIncidentQuery(incidentId);

  return (
    <div className="py-6" data-testid="incident-detail-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
        <Link
          to={`/parent/${studentId}/incidents`}
          data-testid="incident-detail-back"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to incident reports
        </Link>

        {isLoading ? (
          <div
            className="py-12 text-center text-sm text-gray-500"
            data-testid="incident-detail-loading"
          >
            Loading incident…
          </div>
        ) : error || !data ? (
          <div className="py-12 text-center" data-testid="incident-detail-error">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to load incident</h3>
            <p className="mt-1 text-sm text-gray-500">Please try again later.</p>
          </div>
        ) : (
          <IncidentAcknowledgeCard incident={data.incident} />
        )}
      </div>
    </div>
  );
};

export default ParentChildIncidents;
